export type ParentSettingsProfileInput = {
  name?: string | null;
  email?: string | null;
  userSub?: string | null;
  phone?: string | null;
  phoneNumber?: string | null;
  familyName?: string | null;
  primaryContact?: string | null;
  homeAddress?: string | null;
  homePhone?: string | null;
};

export type ParentSettingsProfileDetails = {
  fullName: string;
  familyName: string;
  primaryContact: string;
  email: string;
  phone: string;
  homeAddress: string;
  homePhone: string;
};

export type ParentNotificationPreferenceInput = {
  categoryFilters?: unknown;
  digestFrequency?: unknown;
  muted?: unknown;
};

export type ParentNotificationToggleState = {
  categoryFilters: Set<string>;
  muted: boolean;
};

export const DEFAULT_PARENT_NOTIFICATION_CATEGORIES = [
  'attendance',
  'academic',
  'announcements',
] as const;

export const EMPTY_PARENT_PROFILE_VALUE = 'Not provided';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const readText = (value: unknown): string => String(value ?? '').trim();

const hasMeaningfulValue = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (value && typeof value === 'object') {
    return Object.values(asRecord(value)).some(hasMeaningfulValue);
  }
  return readText(value).length > 0;
};

const readFirstText = (...values: unknown[]): string => {
  for (const value of values) {
    const text = readText(value);
    if (text) return text;
  }
  return '';
};

const joinParts = (parts: unknown[], separator = ' '): string =>
  parts
    .map((part) => readText(part))
    .filter(Boolean)
    .join(separator);

const readName = (record: Record<string, unknown>): string =>
  readFirstText(
    record.fullName,
    record.name,
    joinParts([record.firstName, record.lastName]),
    joinParts([record.givenName, record.familyName]),
  );

const firstRecord = (value: unknown): Record<string, unknown> => {
  if (!Array.isArray(value)) return {};
  return asRecord(value.find(hasMeaningfulValue));
};

const firstNonEmptyRecord = (...records: Record<string, unknown>[]): Record<string, unknown> =>
  records.find(hasMeaningfulValue) ?? {};

const readParentContact = (applicant: Record<string, unknown>, application: Record<string, unknown>): Record<string, unknown> =>
  firstNonEmptyRecord(
    firstRecord(applicant.parents),
    firstRecord(application.parents),
    asRecord(applicant.father),
    asRecord(applicant.mother),
    asRecord(application.father),
    asRecord(application.mother),
  );

const readAddress = (value: unknown): string => {
  const address = asRecord(value);
  const line1 = readFirstText(address.street, address.street1, address.addressLine1, address.address);
  const line2 = readFirstText(address.street2, address.addressLine2, address.unit, address.apartment);
  const cityStateZip = joinParts([
    address.city,
    joinParts([address.state, readFirstText(address.zip, address.zipCode, address.postalCode)], ' '),
  ], ', ');
  return joinParts([line1, line2, cityStateZip], ', ');
};

const withFallback = (value: string): string => value || EMPTY_PARENT_PROFILE_VALUE;

export const buildParentSettingsProfileDetails = ({
  profile,
  application,
}: {
  profile?: ParentSettingsProfileInput | null;
  application?: Record<string, unknown> | null;
}): ParentSettingsProfileDetails => {
  const profileRecord = asRecord(profile);
  const applicationRecord = asRecord(application);
  const applicant = asRecord(applicationRecord.applicant);
  const applicantRecord = Object.keys(applicant).length ? applicant : applicationRecord;
  const primaryContact = asRecord(applicantRecord.primaryContact);
  const parentContact = readParentContact(applicantRecord, applicationRecord);

  const primaryContactName = readFirstText(
    profileRecord.primaryContact,
    readName(primaryContact),
    applicantRecord.primaryContactName,
    readName(parentContact),
    profileRecord.name,
  );
  const fullName = readFirstText(
    profileRecord.fullName,
    profileRecord.name,
    readName(primaryContact),
    readName(parentContact),
    profileRecord.email,
  );

  return {
    fullName: withFallback(fullName),
    familyName: withFallback(readFirstText(profileRecord.familyName, applicantRecord.familyName, applicationRecord.familyName)),
    primaryContact: withFallback(primaryContactName),
    email: withFallback(readFirstText(profileRecord.email, primaryContact.email, parentContact.email)),
    phone: withFallback(readFirstText(
      profileRecord.phone,
      profileRecord.phoneNumber,
      primaryContact.phone,
      primaryContact.cellPhone,
      parentContact.phone,
      parentContact.cellPhone,
    )),
    homeAddress: withFallback(readFirstText(
      profileRecord.homeAddress,
      readAddress(primaryContact.address),
      readAddress(applicantRecord.address),
      readAddress(applicationRecord.homeAddress),
      readAddress(applicationRecord.address),
    )),
    homePhone: withFallback(readFirstText(
      profileRecord.homePhone,
      applicantRecord.homePhone,
      primaryContact.homePhone,
      applicationRecord.homePhone,
    )),
  };
};

const sanitizeCategoryFilters = (value: unknown): Set<string> =>
  new Set(
    (Array.isArray(value) ? value : [])
      .map((entry) => readText(entry))
      .filter(Boolean),
  );

export const resolveInitialParentNotificationCategories = (
  preference?: ParentNotificationPreferenceInput | null,
): Set<string> => {
  if (Boolean(preference?.muted)) {
    return new Set<string>();
  }
  if (Array.isArray(preference?.categoryFilters)) {
    return sanitizeCategoryFilters(preference?.categoryFilters);
  }
  return new Set(DEFAULT_PARENT_NOTIFICATION_CATEGORIES);
};

export const resolveParentNotificationCategoryToggle = (
  currentCategories: Iterable<string>,
  currentMuted: boolean,
  category: string,
  next: boolean,
): ParentNotificationToggleState => {
  const categoryFilters = new Set(currentCategories);
  if (next) categoryFilters.add(category);
  else categoryFilters.delete(category);
  return {
    categoryFilters,
    muted: next ? false : currentMuted,
  };
};

export const resolveParentNotificationMuteToggle = (
  currentCategories: Iterable<string>,
  next: boolean,
): ParentNotificationToggleState => ({
  categoryFilters: next ? new Set<string>() : new Set(currentCategories),
  muted: next,
});
